import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Screen } from "../components/Screen";
import { Ref } from "../components/Ref";
import { Tag } from "../components/Tag";
import { Reveal } from "../components/Reveal";
import { Segmented } from "../components/Segmented";
import { Placeholder } from "../components/Placeholder";
import { MoaDiagram } from "../illustrations/MoaDiagram";
import { useSnak } from "../snak/SnakContext";
const STEPS = [{
  title: "CCR4 on malignant T cells",
  body: "C-C chemokine receptor 4 (CCR4) is expressed on malignant T cells in MF and SS and contributes to their homing to the skin. It is also expressed on some normal T-cell subsets, including regulatory T cells."
}, {
  title: "The antibody binds CCR4",
  body: "The product is a humanised IgG1 monoclonal antibody directed against CCR4."
}, {
  title: "Antibody-dependent cellular cytotoxicity",
  body: "Its Fc region is defucosylated, which increases binding to Fcγ receptor IIIa on effector cells such as NK cells and enhances antibody-dependent cellular cytotoxicity (ADCC) against CCR4-expressing cells."
}];
export function P6Treatment() {
  const {
    explore
  } = useSnak();
  const [view, setView] = useState<"moa" | "evidence">("moa");
  const [step, setStep] = useState(0);
  const [touched, setTouched] = useState(false);

  // Until the HCP takes over, walk through the mechanism once; any tap hands control back.
  useEffect(() => {
    if (touched || view !== "moa" || step >= STEPS.length - 1) return;
    const t = setTimeout(() => setStep(s => s + 1), step === 0 ? 3200 : 2800);
    return () => clearTimeout(t);
  }, [touched, view, step]);
  return <Screen overline="The treatment" refTarget={4} footnote={<p data-id="element-400" data-mp="7gjyvg7ri2x6">
          Indication per EU SmPC; consult the locally applicable prescribing information.<Ref n={4} data-id="element-401" data-mp="cdtlko3ro0hb" />{" "}
          <Placeholder data-id="element-402" data-mp="cf4xxcba2gk1">CH/AT wording if different</Placeholder> Mechanism shown schematically. No superiority over other
          treatments is claimed.
        </p>} data-id="element-399" data-mp="26rb7k6oiwpa8">
      <Reveal delay={0.1} data-id="element-403" data-mp="ddfop8s3inr">
        <h1 className="mt-5 font-display text-[38px] font-medium leading-[1.05] tracking-[-0.01em]" data-id="element-404" data-mp="e4zl2t0jry7">
          Brand name<sup className="text-[0.42em]" data-id="element-405" data-mp="ea0b2nl320rk">®</sup>
          <span className="block italic text-[var(--s-accent)]" data-id="element-406" data-mp="9cqpxk4q8b9j">(generic name).</span>
        </h1>
      </Reveal>
      <Reveal delay={0.2} data-id="element-407" data-mp="nbq41xum57rg">
        <div className="mt-5 border-l-2 border-[var(--s-accent)] pl-4" data-id="element-408" data-mp="nayk5kadoyh0">
          <p className="overline text-[10px] text-[var(--s-accent)]" data-id="element-409" data-mp="ja4rkdlzqvhs">Approved indication (EU)</p>
          <p className="mt-1.5 font-display text-[19px] leading-snug" data-id="element-410" data-mp="x1jeuwmxliun">
            Adults with mycosis fungoides (MF) or Sézary syndrome (SS) who have received at least one prior systemic
            therapy.<Ref n={4} data-id="element-411" data-mp="171traf156m8r" />
          </p>
        </div>
      </Reveal>
      <Reveal delay={0.3} className="mt-7" data-id="element-412" data-mp="121xo2i09qyu9">
        <Segmented options={[{
        value: "moa",
        label: "Mechanism of action"
      }, {
        value: "evidence",
        label: "Clinical evidence"
      }]} value={view} onChange={v => {
        setView(v as "moa" | "evidence");
        setTouched(true);
        explore();
      }} data-id="element-413" data-mp="122yxki1mzd9v" />

        <AnimatePresence mode="popLayout" initial={false} data-id="element-414" data-mp="1goeb2t2b88pz">
          {view === "moa" ? <motion.div key="moa" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25
        }} className="mt-5" data-id="element-415" data-mp="11zi30w096vpd">
              <div className="-mx-2" data-id="element-416" data-mp="t03azs4fud2r">
                <MoaDiagram step={step} data-id="element-417" data-mp="swz24hg12t5r" />
              </div>
              <ol className="mt-2 grid grid-cols-3 gap-2" data-id="element-418" data-mp="xyoblsnlqyrq">
                {STEPS.map((s, i) => {
              const on = i === step;
              return <li key={s.title} data-id="element-419" data-mp="nzyuw37fzkd2" data-mpi={i}>
                      <button onClick={() => {
                  setStep(i);
                  setTouched(true);
                  explore();
                }} className={`flex h-full w-full flex-col items-start gap-1 border-t-2 pt-2 text-left text-[12px] leading-snug transition-[border-color,opacity] duration-200 ${on ? "border-[var(--s-accent)] opacity-100" : "border-[var(--s-rule)] opacity-60 hover:opacity-100"}`} data-id="element-420" data-mp="nyxjfxmikvut">
                        <span className="font-display text-[22px] leading-none text-[var(--s-accent)]" data-id="element-421" data-mp="13k8x3drlsqko">{i + 1}</span>
                        <span className={on ? "font-semibold" : ""} data-id="element-422" data-mp="ymh38e7eeudu">{s.title}</span>
                      </button>
                    </li>;
            })}
              </ol>
              <div className="mt-3 min-h-[84px]" data-id="element-423" data-mp="2szggi0ob3chy">
                <AnimatePresence mode="popLayout" initial={false} data-id="element-424" data-mp="2o157fs5u560s">
                  <motion.p key={step} initial={{
                opacity: 0,
                y: 4
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.25,
                ease: [0.23, 1, 0.32, 1]
              }} className="text-[15px] leading-relaxed" data-id="element-425" data-mp="2nysphf02uvnq">
                    {STEPS[step].body}
                    <Ref n={4} data-id="element-426" data-mp="3abdj6eol2x0z" />
                  </motion.p>
                </AnimatePresence>
              </div>
            </motion.div> : <motion.div key="evidence" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25
        }} className="mt-5" data-id="element-427" data-mp="2hloiy3jp710f">
              <EvidenceTable data-id="element-428" data-mp="1thgxs04uel0" />
            </motion.div>}
        </AnimatePresence>
      </Reveal>
    </Screen>;
}
function EvidenceTable() {
  const rows = [{
    tag: <Tag kind="trial" label="MAVORIC" data-id="element-429" data-mp="6uophta3nhsq" />,
    design: "Phase 3, randomised, open-label, vs vorinostat",
    population: "372 adults with previously treated MF or SS",
    role: "Pivotal randomised evidence",
    ref: 2
  }, {
    tag: <Tag kind="evidence" label="MINT" data-id="element-430" data-mp="2bwolyhoip24h" />,
    design: "Real-world, observational, non-randomised",
    population: "109 patients, 18 German centers",
    role: "Outcomes in routine German care (provisional)",
    ref: 1
  }, {
    tag: <Tag kind="guidance" label="Expert recommendations" data-id="element-431" data-mp="1k34nnjitlc3f" />,
    design: "Expert guidance",
    population: "Germany",
    role: "Clinical positioning",
    ref: 3
  }];
  return <div data-id="element-432" data-mp="1syu5xjoz1qcl">
      <div className="divide-y divide-[var(--s-rule)] border-y border-[var(--s-rule)]" data-id="element-433" data-mp="1sznf0rbekrxw">
        {rows.map(r => <div key={r.ref} className="py-4" data-id="element-434" data-mp="10h8b3fzkb4dt">
            <span data-id="element-435" data-mp="10fx83idxy0nr">
              {r.tag}
              <Ref n={r.ref} data-id="element-436" data-mp="10evkyfjnhnpo" />
            </span>
            <p className="mt-1.5 font-display text-[18px] leading-snug" data-id="element-437" data-mp="welhpan8tbei">{r.design}</p>
            <p className="mt-1 text-[13px] text-[var(--s-muted)]" data-id="element-438" data-mp="mbg2w9btqny4">
              {r.population} · {r.role}
            </p>
          </div>)}
      </div>
      <p className="mt-3 text-[11.5px] leading-relaxed text-[var(--s-muted)]" data-id="element-439" data-mp="r499z3urm8dd">
        Designs differ; results from these sources should not be compared directly. No superiority over other treatments
        is claimed.
      </p>
    </div>;
}